/**
 * All 왁뿌볼 sounds are synthesized; nothing is sampled. When the context is built, a small bank of one-shot
 * sounds is rendered in plain JS (~1.8 s of audio, ~6 ms of work), and every event plays 1–9 of them: one buffer source
 * + one gain each, so a hit costs 2 nodes instead of a dozen live filters.
 *
 * What a real wax-over-clay ball sounds like, and what is imitated here:
 *   - The candle-wax shell is brittle and thin, glued to soft clay: a crack is a sharp, dry, broadband click
 *     (energy 1.5–7 kHz, softer than glass so little sparkle above ~9 kHz) with a short mid "crunch" body
 *     (0.5–2 kHz). The clay damps the shell, so nothing rings: every tail is gone within ~40 ms.
 *   - Crisp = one sharp high event; crunchy = a series of smaller, lower events. Under a finger the shell fails as a
 *     cascade ("콰자작"): the first crack is the biggest, the next ones smaller, tens of ms apart, with silence between.
 *     Bigger cracks are lower and heavier; hairlines are tiny, high ticks.
 *   - A loose plate breaking free first creaks (its edge scraping its neighbours), then snaps off ("트득-똑").
 *   - A free flake hitting the table rings very briefly (a light, lossy plate: a dry "틱", higher when smaller);
 *     a big piece with clay on it lands with a dull "툭". Several landing together are separate ticks ("타다닥").
 *   - The clay core is quiet and low: a soft, wet "뭉/쭈욱" with a little sticky "쩍" when the finger moves.
 * Style rules: discrete hits only, never a continuous hiss; iPhone (no vibration) gets a 110–185 Hz punch with strong
 * 2nd–4th harmonics, which a phone speaker can actually play; a voice budget keeps the summed peak below full scale.
 */
const db = (x: number) => Math.pow(10, x / 20);
const U = (a: number, b: number) => a + (b - a) * Math.random();
const clamp01 = (x: number) => (x > 0 ? (x < 1 ? x : 1) : 0); // NaN → 0
/** a per-ball pitch factor; a non-finite or non-positive one (a caller bug) plays at 1 instead of throwing */
const pitch = (m: number) => (m > 0 && m < Infinity ? m : 1);

/**
 * A live layer (the wet kneading): its driven params `p` (gain last) ramp from `a` at `t0` to `b` at `t1`. The ramp is
 * kept here because a param's `.value` lags: a fresh node still reports its default (gain 1) until audio runs.
 */
type Layer = { nodes: AudioNode[]; p: AudioParam[]; a: Float32Array; b: Float32Array; t0: number; t1: number };
type HitKind = "kwa" | "ja" | "jak" | "tik";
type FilterKind = "highpass" | "bandpass" | "lowpass";
type SndKind = HitKind | "fric" | "peel" | "landS" | "landL" | "squish" | "sub" | "heavy";
/**
 * one rendered sound: `env` is its peak |x| per ms (dilated ±1 ms), `marks` the onsets (ms) of its audible hits, `loud`
 * the last ms whose envelope the budget looks at (≥ QUIET)
 */
type Snd = { buf: AudioBuffer; env: Float32Array; marks: number[]; loud: number };
/**
 * Voice lanes. LO: small sounds (ticks, lands, squish), budgeted under CEIL_LO. TAIL: a crunch's later hits. MAIN: other
 * loud sounds. HEAD: a crunch's first hit and its low body; when the budget would duck it, older events' LO and TAIL
 * voices in its way give way (see giveWay()), so the newest crack is never the one that loses.
 */
const LO = 0, TAIL = 1, MAIN = 2, HEAD = 3;
type Lane = typeof LO | typeof TAIL | typeof MAIN | typeof HEAD;
/** `cut`: silent from this time on (gave way); `ev` the event that played it */
type Voice = {
  t: number; r: number; g: number; env: Float32Array; end: number; cut: number; lane: Lane; ev: number;
  src: AudioBufferSourceNode | null; gn: GainNode | null;
};
type Land = { size: number; sp: number; m: number; e: number };

export type CrunchOpts = {
  /** the main pop: adds the "뽀" body under the crunch */
  heavy?: boolean;
  /** extra low punch: used where the phone cannot vibrate (iPhone) */
  sub?: boolean;
};

// onset patterns (ms) for 1–4 hits: 콱 / 콰작 / 콰자작 / 콰자자작
const ONSETS = [[0], [0, 74], [0, 68, 122], [0, 60, 108, 150]];
const KINDS: HitKind[][] = [["kwa"], ["kwa", "jak"], ["kwa", "ja", "jak"], ["kwa", "ja", "ja", "jak"]];
const LEAD = 0.005; // scheduling lead (s): keeps the first touch well inside 50 ms
const CEIL_HI = 0.92; // summed peak bound for crunch hits (under the safety knee at 0.93)
const CEIL_LO = 0.5; // ...and for small sounds (ticks, lands, squish): they never take the crunch's headroom
const MAX_HI = 32, MAX_LO = 20; // concurrent voices
const QUIET = 0.03; // envelope below this (of a sound's own peak) is too quiet to be budgeted

// ---------- offline synthesis (plain JS, runs once per context) ----------

type Rnd = () => number;
const mulberry = (seed: number): Rnd => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** RBJ biquad (bandpass = 0 dB peak, like BiquadFilterNode), linear Q */
function coefs(type: FilterKind, f: number, q: number, sr: number) {
  const w = (2 * Math.PI * Math.min(f, sr * 0.45)) / sr, c = Math.cos(w), al = Math.sin(w) / (2 * q), a0 = 1 + al;
  const b0 = type === "lowpass" ? (1 - c) / 2 : type === "highpass" ? (1 + c) / 2 : al;
  const b1 = type === "lowpass" ? 1 - c : type === "highpass" ? -(1 + c) : 0;
  const b2 = type === "bandpass" ? -al : b0;
  return [b0 / a0, b1 / a0, b2 / a0, (-2 * c) / a0, (1 - al) / a0];
}

class Synth {
  o: Float32Array;
  sr: number;
  rnd: Rnd;
  private tmp: Float32Array;
  constructor(ms: number, sr: number, rnd: Rnd, tmp: Float32Array) {
    this.sr = sr; this.rnd = rnd; this.tmp = tmp;
    this.o = new Float32Array(Math.ceil((ms * sr) / 1000));
  }
  R(a: number, b: number) { return a + (b - a) * this.rnd(); }
  private at(ms: number) { return Math.round((ms * this.sr) / 1000); }

  /** filtered noise: `att` ms linear attack, then exp decay `tau` ms; level normalised so `peak` ≈ its peak */
  noise(ms: number, type: FilterKind, f: number, q: number, peak: number, tau: number, att = 0.15, sweepTo = 0) {
    const o = this.o, sr = this.sr, x = this.tmp, s0 = this.at(ms);
    const n = Math.min(o.length - s0, this.at(att + tau * 7)), nr = Math.max(n, this.at(6));
    if (n <= 0) return;
    let k = coefs(type, f, q, sr), x1 = 0, x2 = 0, y1 = 0, y2 = 0, ss = 0;
    for (let i = 0; i < nr; i++) {
      if (sweepTo && (i & 31) === 0) k = coefs(type, f * Math.pow(sweepTo / f, Math.min(1, i / (n * 0.6))), q, sr);
      const v = this.rnd() * 2 - 1;
      const y = k[0] * v + k[1] * x1 + k[2] * x2 - k[3] * y1 - k[4] * y2;
      x2 = x1; x1 = v; y2 = y1; y1 = y; x[i] = y; ss += y * y;
    }
    const g = peak / (3 * Math.sqrt(ss / nr) + 1e-9), a = Math.max(1, this.at(att)), dk = Math.exp(-1000 / (tau * sr));
    for (let i = 0, e = g; i < n; i++) {
      if (i < a) o[s0 + i] += (x[i] * g * i) / a;
      else { o[s0 + i] += x[i] * e; e *= dk; }
    }
  }

  /** a bipolar fracture pulse (derivative of a gaussian): the crisp edge of a crack; `w` ms sets its brightness */
  click(ms: number, peak: number, w: number) {
    const o = this.o, sg = (w * this.sr) / 1000, c = this.at(ms) + Math.ceil(3 * sg);
    for (let i = Math.max(0, Math.floor(c - 4 * sg)); i < Math.min(o.length, c + 4 * sg); i++) {
      const u = (i - c) / sg;
      o[i] += -peak * u * Math.exp(0.5 - 0.5 * u * u);
    }
  }

  /** a damped partial (a free flake's brief ring) */
  mode(ms: number, f: number, tau: number, peak: number) {
    const o = this.o, sr = this.sr, s0 = this.at(ms), n = Math.min(o.length - s0, this.at(tau * 6)), a = this.at(0.1);
    const ph = this.rnd() * 6.283, w = (6.283185 * f) / sr, d = 1000 / (tau * sr);
    for (let i = 0; i < n; i++) o[s0 + i] += peak * Math.sin(ph + w * i) * Math.exp(-i * d) * Math.min(1, i / a);
  }

  /** a falling-pitch tone with harmonics (thumps, the iPhone punch) */
  sweep(ms: number, f0: number, f1: number, dur: number, tau: number, peak: number, harm: number[], att = 1.5) {
    const o = this.o, sr = this.sr, s0 = this.at(ms), n = Math.min(o.length - s0, this.at(Math.max(dur, tau * 6)));
    const a = this.at(att), dk = Math.exp(-1000 / (tau * sr)), nd = this.at(dur), fk = Math.pow(f1 / f0, 1 / nd);
    let ph = 0, hs = 0, f = f0, e = peak;
    for (const h of harm) hs += h;
    for (let i = 0; i < n; i++) {
      ph += (6.283185 * f) / sr;
      if (i < nd) f *= fk;
      let v = 0;
      for (let h = 0; h < harm.length; h++) v += harm[h] * Math.sin(ph * (h + 1));
      if (i < a) o[s0 + i] += (peak * v * i) / a / hs;
      else { o[s0 + i] += (e * v) / hs; e *= dk; }
    }
  }

  /** micro-cracks inside a hit: tiny clicks whose size falls off with time (the shell settling) */
  grit(from: number, to: number, count: number, lvl: number, f: number) {
    for (let i = 0; i < count; i++) {
      const ms = this.R(from, to), fall = 1 - 0.6 * ((ms - from) / (to - from));
      this.click(ms, lvl * this.R(0.5, 1) * fall, this.R(0.04, 0.07));
      this.noise(ms, "highpass", f * this.R(0.85, 1.2), 0.7, lvl * this.R(0.4, 0.9) * fall, this.R(0.35, 0.7), 0.05);
    }
  }
}

/** the per-variant recipes; `s.R` keeps every variant a little different */
function recipe(kind: SndKind, s: Synth) {
  const R = (a: number, b: number) => s.R(a, b);
  switch (kind) {
    case "kwa": // the big hit: crack edge + crunch body + lower body + grit + a short weight thump
      s.click(0, 1, R(0.07, 0.1));
      s.noise(0, "bandpass", R(2900, 3600), 0.6, 0.9, R(1, 1.5));
      s.noise(0.3, "bandpass", R(1050, 1300), 1.0, 0.85, R(9, 12), 0.6);
      s.noise(0.5, "bandpass", R(430, 520), 1.3, 0.5, R(7, 9), 1);
      s.grit(2, 20, 3 + Math.floor(R(0, 3)), 0.45, 2700);
      s.sweep(0, R(165, 180), 90, 35, 9, 0.3, [1, 0.3]);
      return [0];
    case "ja": // a smaller, drier follow-up
      s.click(0, 0.8, R(0.06, 0.08));
      s.noise(0, "bandpass", R(3200, 3900), 0.6, 0.75, R(0.8, 1.1));
      s.noise(0.3, "bandpass", R(1350, 1650), 1.3, 0.6, R(5.5, 7.5), 0.5);
      s.grit(2, 12, 1 + Math.floor(R(0, 2)), 0.35, 2900);
      s.sweep(0, R(145, 160), 95, 25, 6, 0.15, [1, 0.3]);
      return [0];
    case "jak": // the closing snap
      s.click(0, 1, R(0.05, 0.07));
      s.noise(0, "bandpass", R(3600, 4300), 0.7, 0.95, R(0.8, 1));
      s.noise(0.2, "bandpass", R(1850, 2200), 1.6, 0.55, R(4, 5.5), 0.4);
      s.noise(0.4, "bandpass", R(600, 700), 2.0, 0.3, R(3.5, 4.5), 0.6);
      return [0];
    case "tik": // a hairline: one tiny, high, dry tick
      s.click(0, R(0.5, 0.8), R(0.035, 0.055));
      s.noise(0, "highpass", R(3200, 4000), 0.7, 1, R(0.35, 0.8), 0.05);
      if (s.rnd() < 0.6) s.noise(0.2, "bandpass", R(1800, 2400), 2.2, R(0.25, 0.5), R(1.5, 3.5), 0.3);
      return [0];
    case "fric": // one dry friction grain of the wax resisting the finger (pitched at 1 kHz; played at f/1000)
      s.noise(0, "bandpass", 1000, 5, 1, R(1.5, 3), 0.3);
      if (s.rnd() < 0.4) s.noise(0, "highpass", 4200, 0.7, 0.3, 0.4, 0.05);
      return [0];
    case "peel": { // a plate breaking free: its edge creaks against the neighbours, then it snaps off
      s.click(0, 0.45, 0.05);
      s.noise(0, "highpass", 3000, 0.7, 0.45, 0.5, 0.05);
      const snap = R(17, 26);
      for (let i = 0, n = 3 + Math.floor(R(0, 3)); i < n; i++) {
        const ms = 3 + ((snap - 5) * (i + R(0.2, 0.8))) / n;
        s.noise(ms, "bandpass", 1800 + 900 * (ms / snap) * R(0.9, 1.1), 2, R(0.22, 0.42), R(0.8, 1.6), 0.2);
      }
      s.click(snap, 1, R(0.05, 0.07));
      s.noise(snap, "bandpass", R(3200, 3800), 0.7, 0.85, R(0.8, 1));
      s.noise(snap + 0.2, "bandpass", R(1500, 1750), 1.4, 0.6, R(4.5, 6), 0.4);
      s.noise(snap + 0.4, "bandpass", R(480, 560), 1.5, 0.3, R(5, 7), 0.6);
      return [0, snap];
    }
    case "landS": { // a small flake landing: contact click + a very short ring of a free, lossy plate
      const f0 = R(2600, 3600);
      s.click(0, 0.8, R(0.035, 0.05));
      s.mode(0, f0, R(1.1, 1.8), 0.8);
      s.mode(0, f0 * R(2.6, 2.9), R(0.7, 1.1), 0.4);
      s.mode(0, f0 * R(1.45, 1.6), R(0.8, 1.3), 0.3);
      s.noise(0, "highpass", 3000, 0.7, 0.4, 0.4, 0.05);
      s.noise(0.1, "bandpass", R(1700, 2300), 1.2, 0.35, R(1.2, 2), 0.2);
      if (s.rnd() < 0.5) { const t2 = R(5, 11); s.click(t2, 0.25, 0.04); s.mode(t2, f0 * 1.06, 1, 0.25); } // it rocks once
      return [0];
    }
    case "landL": { // a big piece (clay on its back): lower ring + a dull clay "툭", often a second contact as it rocks
      const f0 = R(1050, 1500);
      s.click(0, 0.7, R(0.08, 0.11));
      s.mode(0, f0, R(2.8, 4), 0.7);
      s.mode(0, f0 * R(2.2, 2.4), R(1.5, 2.2), 0.35);
      s.noise(0.2, "bandpass", R(290, 350), 1, 0.6, R(6, 8), 0.8);
      s.sweep(0, R(220, 240), 150, 20, 7, 0.3, [1, 0.4]);
      const t2 = R(8, 16); s.click(t2, 0.3, 0.06); s.mode(t2, f0 * 1.08, 1.6, 0.3);
      return [0];
    }
    case "squish": { // the bare clay giving under the finger: soft low body + a wet formant sliding down + sticky ticks
      s.noise(0, "lowpass", R(420, 500), 0.8, 0.9, R(26, 32), R(9, 12));
      s.noise(0, "bandpass", R(600, 700), 4, 0.75, R(30, 38), 7, R(240, 280));
      s.sweep(0, R(115, 125), 70, 70, 28, 0.3, [1, 0.5, 0.25], 7);
      for (let i = 0, n = 2 + Math.floor(R(0, 2)); i < n; i++) s.noise(R(45, 105), "bandpass", R(1200, 2000), 3, R(0.06, 0.12), 0.8, 0.1);
      return [0];
    }
    case "sub": // iPhone punch: 178→114 Hz (stays in 110–185 Hz at ±3 % rate), strong 2nd–4th harmonics
      s.sweep(0, 178, 114, 36, 10, 1, [1, 0.6, 0.4, 0.2]);
      return [0];
    case "heavy": // "뽀": the ball giving way under the first hit
      s.sweep(0, R(135, 145), 72, 55, 13, 0.9, [1, 0.3]);
      s.noise(0, "lowpass", 320, 0.8, 0.8, 9, 1);
      return [0];
  }
}

const BANK: [SndKind, number, number][] = [ // kind, variants, length (ms)
  ["kwa", 4, 75], ["ja", 4, 50], ["jak", 3, 40], ["tik", 6, 16], ["fric", 4, 15], ["peel", 4, 60],
  ["landS", 5, 26], ["landL", 3, 60], ["squish", 3, 150], ["sub", 1, 70], ["heavy", 2, 90],
];

/** Renders the bank at the context's rate (~6 ms on a desktop; run at prewarm, not on a touch). */
export function renderBank(ctx: BaseAudioContext) {
  const sr = ctx.sampleRate, tmp = new Float32Array(Math.ceil(sr * 0.4));
  const bank = {} as Record<SndKind, Snd[]>;
  let seed = 0x5eed;
  for (const [kind, count, ms] of BANK) {
    const list: Snd[] = [];
    for (let v = 0; v < count; v++) {
      const s = new Synth(ms, sr, mulberry(seed++), tmp);
      const marks = recipe(kind, s);
      const o = s.o;
      let pk = 0, end = 0;
      for (let i = 0; i < o.length; i++) { const a = Math.abs(o[i]); if (a > pk) pk = a; }
      for (let i = 0; i < o.length; i++) { o[i] /= pk; if (Math.abs(o[i]) > 3e-4) end = i; }
      const len = end + 1, fade = Math.min(len, Math.round(sr * 0.002));
      for (let i = 0; i < fade; i++) o[len - 1 - i] *= i / fade; // no click at the cut
      const buf = ctx.createBuffer(1, len, sr);
      buf.getChannelData(0).set(o.subarray(0, len));
      // peak per ms, dilated by ±1 ms so a voice starting anywhere inside a ms is still bounded
      const nb = Math.ceil((len * 1000) / sr), raw = new Float32Array(nb), env = new Float32Array(nb + 1);
      for (let i = 0; i < len; i++) { const b = Math.floor((i * 1000) / sr), a = Math.abs(o[i]); if (a > raw[b]) raw[b] = a; }
      for (let b = 0; b <= nb; b++) env[b] = Math.max(raw[b - 1] ?? 0, raw[b] ?? 0, raw[b + 1] ?? 0);
      let loud = 0;
      for (let b = 0; b < env.length; b++) if (env[b] >= QUIET) loud = b;
      list.push({ buf, env, marks, loud });
    }
    bank[kind] = list;
  }
  return bank;
}

export class CrushAudio {
  ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private bank: Record<SndKind, Snd[]> | null = null;
  private lastPick = new Map<SndKind, number>();
  private voices: Voice[] = [];
  /** the current event (a crunch); voices remember it so a crack's first hit only displaces older ones */
  private ev = 0;
  private layers = new Map<string, Layer>();
  private tgt = new Float32Array(4);
  /** per ball: crack run (rad) not yet turned into ticks */
  private runAcc = new Map<string, number>();
  private squeezeAcc = new Map<string, number>();
  private runTotal = new Map<string, number>();
  /** recent grain times (s), so dense moments never pile up into a hiss */
  private grainTimes: number[] = [];
  private lastHair = -1;
  private flakeNext = 0;
  private landNext = 0;
  private landQ: Land[] = [];
  private landQueued = false;
  private landTimes: number[] = [];
  private squishAcc = 0;
  /** when the last crunch started (s): the running-crack crackle only trails it briefly */
  private lastCrunch = -1;
  private lastSquish = -1;
  onState: (running: boolean) => void = () => {};
  wanted = false; // a press happened; used to show the "소리 켜기" chip when locked

  get running() {
    return !!this.ctx && this.ctx.state === "running";
  }

  /** Create/resume on a user gesture. Safe to call often. */
  unlock() {
    try {
      if (!this.ctx || this.ctx.state === "closed") this.build();
      const ctx = this.ctx!;
      const done = () => {
        if (ctx.state !== "running") { this.onState(false); return; }
        const b = ctx.createBuffer(1, 1, ctx.sampleRate);
        const s = ctx.createBufferSource(); s.buffer = b; s.connect(ctx.destination); s.start();
        const nav = navigator as Navigator & { audioSession?: { type: string } };
        if (nav.audioSession && nav.audioSession.type !== "playback") nav.audioSession.type = "playback";
        this.onState(true);
      };
      if (ctx.state !== "running") ctx.resume().then(done, () => this.onState(false));
      else done();
    } catch {
      this.onState(false);
    }
  }

  /**
   * Builds the (still suspended) context and renders the sound bank ahead of time, at idle. Creating an AudioContext
   * costs ~100–200 ms; done inside the first pointerdown it froze the very first touch. The gesture then only resumes it.
   */
  prewarm() {
    try { if (!this.ctx || this.ctx.state === "closed") this.build(); } catch { /* no Web Audio: stay silent */ }
  }

  /** Resume only if a context already exists (e.g. tab became visible again). */
  resumeExisting() {
    const ctx = this.ctx;
    if (!ctx || ctx.state === "closed" || ctx.state === "running") return;
    ctx.resume().then(() => this.onState(ctx.state === "running"), () => {});
  }

  private build() {
    const ctx = new AudioContext();
    const master = ctx.createGain(); master.gain.value = 1;
    // levels are budgeted per voice (see room()), so no compressor pumping the transients; this soft knee above 0.93
    // is only a safety net and never touched by design
    const safe = ctx.createWaveShaper(), c = new Float32Array(1025);
    for (let i = 0; i < c.length; i++) {
      const x = i / 512 - 1, a = Math.abs(x);
      c[i] = a <= 0.93 ? x : Math.sign(x) * (0.93 + 0.06 * Math.tanh((a - 0.93) / 0.06));
    }
    safe.curve = c;
    master.connect(safe); safe.connect(ctx.destination);
    const noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 2), ctx.sampleRate);
    const d = noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.ctx = ctx; this.master = master; this.noise = noise;
    this.bank = renderBank(ctx);
    this.layers.clear(); this.voices.length = 0; this.landQ.length = 0;
    this.lastHair = this.lastSquish = -1; this.flakeNext = this.landNext = 0;
  }

  dispose() {
    for (const l of this.layers.values()) l.nodes.forEach((n) => { try { (n as AudioScheduledSourceNode).stop?.(); } catch { /* */ } });
    this.layers.clear(); this.voices.length = 0; this.landQ.length = 0;
    this.ctx?.close().catch(() => {});
    this.ctx = null;
  }

  private now() { return this.ctx!.currentTime; }

  // ---------- voices ----------

  /** a different variant than last time, so repeats never sound machine-made */
  private pick(kind: SndKind) {
    const a = this.bank![kind], last = this.lastPick.get(kind) ?? -1;
    let i = Math.floor(Math.random() * a.length);
    if (a.length > 1 && i === last) i = (i + 1 + Math.floor(Math.random() * (a.length - 1))) % a.length;
    this.lastPick.set(kind, i);
    return a[i];
  }

  /**
   * The largest gain a sound may play at `t` (rate `r`) so that Σ|all voices| stays ≤ `ceil` over its whole length.
   * Uses each sound's per-ms peak envelope, so it is a hard bound (triangle inequality): no clipping, ever.
   */
  private room(env: Float32Array, t: number, r: number, ceil: number, spare = false) {
    let best = Infinity;
    const n = Math.ceil(env.length / r), base = this.layers.size * 0.08;
    for (let j = 0; j < n; j++) {
      const e = env[Math.min(env.length - 1, Math.floor(j * r))];
      if (e < QUIET) continue;
      const tj = t + j / 1000;
      let sum = base;
      for (const v of this.voices) {
        if (tj >= v.cut || (spare && this.yields(v))) continue;
        const x = Math.floor((tj - v.t) * 1000 * v.r);
        if (x >= -1 && x < v.env.length) sum += v.g * v.env[Math.max(0, x)];
      }
      const room = (ceil - sum) / e;
      if (room < best) best = room;
    }
    return Math.max(0, best);
  }

  /** Plays one sound: 2 nodes. Returns the gain it got (0 = dropped by the voice budget, or a non-finite input). */
  private play(s: Snd, t: number, gain: number, rate: number, lane: Lane) {
    if (!(gain >= 1e-4 && rate > 0) || !Number.isFinite(gain + rate + t)) return 0;
    const ctx = this.ctx!, now = ctx.currentTime, vs = this.voices, hi = lane !== LO;
    let w = 0;
    for (let i = 0; i < vs.length; i++) if (vs[i].end > now) vs[w++] = vs[i];
    vs.length = w;
    const end = t + s.env.length / 1000 / rate, cap = hi ? MAX_HI : MAX_LO, ceil = hi ? CEIL_HI : CEIL_LO;
    let g = vs.length < cap ? Math.min(gain, this.room(s.env, t, rate, ceil)) : 0;
    // a crack's first hit held down by older tails or small sounds: they give way, but only when that buys it ≥ 1 dB
    // (another crack's head in the same frame, which never gives way, is the usual limit then)
    if (lane === HEAD && g < gain * 0.9) {
      const g2 = Math.min(gain, this.room(s.env, t, rate, ceil, true));
      if (g2 > g * 1.12) { this.giveWay(t, t + (s.loud + 1) / 1000 / rate); g = vs.length < cap ? g2 : 0; }
    }
    if (g < gain * (hi ? 0.2 : 0.3)) return 0;
    const src = ctx.createBufferSource(); src.buffer = s.buf; src.playbackRate.value = rate;
    const gn = ctx.createGain(); gn.gain.value = g;
    src.connect(gn); gn.connect(this.master!); src.start(t);
    vs.push({ t, r: rate, g, env: s.env, end, cut: Infinity, lane, ev: this.ev, src, gn });
    return g;
  }

  /** Whether a voice gives way to a crack's first hit: an older event's later crunch hit or small sound. */
  private yields(v: Voice) { return v.lane <= TAIL && v.ev !== this.ev && v.gn !== null; }

  /**
   * Makes room for a crack's first hit whose budgeted part spans `t`..`last`: every voice that yields() and the budget
   * sees there (room() reads ±1 ms around each ms, hence the margin) goes. One that starts at `t` or later never plays;
   * one already sounding fades out over the 1 ms before `t`.
   */
  private giveWay(t: number, last: number) {
    const vs = this.voices, now = this.ctx!.currentTime;
    let w = 0;
    for (let i = 0; i < vs.length; i++) {
      const v = vs[i];
      if (!this.yields(v) || v.t > last + 0.002 / v.r || v.end <= t) { vs[w++] = v; continue; }
      const gn = v.gn!, src = v.src!;
      if (v.t >= t) { // not started yet: silent, and out of the budget
        gn.gain.setValueAtTime(0, now);
        try { src.stop(); } catch { /* */ }
        continue;
      }
      gn.gain.setValueAtTime(v.g, Math.max(now, t - 0.001));
      gn.gain.linearRampToValueAtTime(0, t);
      try { src.stop(t + 0.002); } catch { /* */ }
      v.cut = v.end = t;
      vs[w++] = v;
    }
    vs.length = w;
  }

  /** Registers a live (non-bank) sound with a rough exp envelope so the budget sees it too. */
  private track(t: number, g: number, tauMs: number, durMs: number) {
    const env = new Float32Array(Math.ceil(durMs) + 2);
    for (let i = 0; i < env.length; i++) env[i] = Math.exp(-Math.max(0, i - 1) / Math.max(0.5, tauMs));
    this.voices.push({ t, r: 1, g, env, end: t + env.length / 1000, cut: Infinity, lane: LO, ev: this.ev, src: null, gn: null });
  }

  // ---------- building blocks ----------

  /** A live filtered noise burst (used only for rare sounds such as tick()): ~0.6 ms attack, exp decay `tauMs`. */
  private noiseHit(t: number, type: FilterKind, f: number, q: number, peak: number, tauMs: number, durMs: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource(); src.buffer = this.noise;
    const flt = ctx.createBiquadFilter(); flt.type = type; flt.frequency.value = Math.min(16000, Math.max(30, f)); flt.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.0006);
    g.gain.setTargetAtTime(0, t + 0.0006, tauMs / 1000);
    src.connect(flt); flt.connect(g); g.connect(this.master!);
    src.start(t, Math.random() * 1.8); src.stop(t + durMs / 1000 + 0.01);
    this.track(t, peak, tauMs, durMs);
  }

  /**
   * One crunch hit. `lvl` is its linear peak, `m` the per-ball pitch, `punch` the level of the iPhone low punch under it
   * (0 = none): it starts 1.5 ms after the crack, as the clay body follows the shell, and dies before the next hit.
   */
  private hit(t: number, kind: HitKind, lvl: number, m: number, punch: number, lane: Lane) {
    const g = this.play(this.pick(kind), t, lvl, m * U(0.92, 1.1), lane);
    if (punch > 0 && g > 0) this.play(this.bank!.sub[0], t + 0.0015, punch, U(0.97, 1.03), lane);
    return g;
  }

  /**
   * One crunch event of `hits` (1–4) hits, e.g. 3 = "콰자작". `levelDb` ≈ the first hit's peak in dBFS.
   * Returns the onsets (ms) of the hits that play (empty when audio is locked) so the caller can line haptics up with them.
   */
  crunch(hits: number, levelDb: number, m: number, opts: CrunchOpts = {}): number[] {
    if (!this.running) return [];
    const n = hits > 1 ? Math.min(4, Math.round(hits)) : 1;
    const t0 = this.now() + LEAD;
    this.lastCrunch = t0;
    const stretch = U(0.94, 1.1);
    const lvl = db(levelDb);
    const kinds = KINDS[n - 1], on = ONSETS[n - 1], out: number[] = [];
    const gains = { kwa: 1, ja: 0.62, jak: 0.74, tik: 1 } as const;
    m = pitch(m); this.ev++;
    // the low body under the first hit: the iPhone punch (which replaces "뽀" there: a phone cannot play 70–140 Hz) or
    // "뽀". The first crack gives it a little headroom, so the voice budget never has to drop it.
    const low = opts.sub || opts.heavy;
    for (let i = 0; i < n; i++) {
      const k = kinds[i], punch = !opts.sub || k === "tik" ? 0 : lvl * (i > 0 ? 0.4 : opts.heavy ? 0.75 : 0.62);
      const ms = i === 0 ? 0 : on[i] * stretch + U(-4, 4);
      // each following hit a little lower too: the crack runs into smaller pieces
      const g = this.hit(t0 + ms / 1000, k, lvl * gains[k] * U(0.88, 1.04) * (i === 0 && low ? 0.8 : 1), m * (1 + 0.03 * i), punch, i === 0 ? HEAD : TAIL);
      if (g > 0) out.push(ms);
    }
    if (opts.heavy && !opts.sub) this.play(this.pick("heavy"), t0 + 0.002, lvl * 0.5, m * U(0.95, 1.05), HEAD);
    return out;
  }

  /**
   * Grain budget: at most `max` grains in any 50 ms window. settle() books its ticks ahead, so times come out of order:
   * every window that would hold `t` is checked, not just the one ending at it.
   */
  private allowGrain(t: number, max = 4) {
    const g = this.grainTimes, W = 0.05, old = this.now() - W;
    let w = 0;
    for (let i = 0; i < g.length; i++) if (g[i] >= old) g[w++] = g[i];
    g.length = w;
    // the busiest window holding t starts at t − W or where a later grain enters it (at that grain − W); 1 µs slack
    // because (g − W) + W can round to just under g
    for (let i = -1; i < w; i++) {
      const s = i < 0 ? t - W : g[i] - W;
      if (s < t - W || s > t) continue;
      let c = 0;
      for (let j = 0; j < w; j++) if (g[j] >= s - 1e-6 && g[j] <= s + W + 1e-6) c++;
      if (c >= max) return false;
    }
    g.push(t); return true;
  }

  /** One tiny crack tick, pitch and level varied. */
  private grain(t: number, m: number, lvl: number, bright = 1) {
    this.play(this.pick("tik"), t, lvl, m * bright * U(0.85, 1.2), LO);
  }

  /** The cracks settling after a crunch: 1–2 sparse, quiet ticks ("크득"), never a texture. */
  private settle(t: number, m: number, count: number) {
    for (let i = 0; i < count; i++) {
      const ti = t + U(0.035, 0.11) + i * 0.04;
      if (this.allowGrain(ti, 2)) this.grain(ti, m, db(U(-41, -35)), 1.1);
    }
  }

  /**
   * 크득크득: a crack front ran `ran` rad this frame. One tick per ~0.03 rad of run, scattered over the next
   * frame, so a fast-running crack crackles densely and a slowing one thins out. The crush's petals are louder and lower.
   */
  crackleRun(key: string, ran: number, m: number, crush = false) {
    if (!this.running || !(ran > 0 && ran < Infinity)) return; // a NaN would stick in the accumulators for good
    // only a short, sparse trail right after a crunch ("크득"), never a long sizzle while cracks keep running
    if (this.now() - this.lastCrunch > 0.22) { this.runAcc.delete(key); return; }
    m = pitch(m);
    // the crush's cracks run round the whole ball at once: sparser ticks that die away as they reach the far side,
    // so it stays a crackle with gaps (크드득 드득 득) and never turns into a hiss
    const total = (this.runTotal.get(key) ?? 0) + ran; this.runTotal.set(key, total);
    const fade = crush ? Math.min(1, total / 22) : 0;
    const step = crush ? 0.07 + 0.08 * fade : 0.12;
    let acc = (this.runAcc.get(key) ?? 0) + ran;
    const t0 = this.now() + LEAD;
    let n = 0;
    while (acc >= step && n < 1) {
      acc -= step; n++;
      const t = t0 + U(0, 0.016);
      if (!this.allowGrain(t, 3)) continue;
      const lvl = crush ? U(-19, -13) - 12 * fade : U(-38, -31);
      this.grain(t, crush ? m * 0.8 : m, db(lvl), crush ? 0.8 : 1);
      // now and then a slightly bigger snap inside the run
      if (Math.random() < (crush ? 0.2 * (1 - fade) : 0.1)) this.play(this.pick("jak"), t + 0.002, db(crush ? -17 - 8 * fade : -34), m * U(1, 1.2), LO);
    }
    this.runAcc.set(key, Math.min(acc, step * 2));
  }

  /**
   * 뽀드득: the finger squeezing intact wax. `amount` is how much the dent deepened (fraction of R);
   * a grain every ~0.012 R of squeeze, quiet and dry, darker as the ball gives.
   */
  squeeze(key: string, amount: number, m: number, depth: number) {
    if (!this.running || !(amount > 0 && amount < Infinity)) return; // a NaN would stick in the accumulator for good
    let acc = (this.squeezeAcc.get(key) ?? 0) + amount;
    const step = 0.012, t0 = this.now() + LEAD;
    let n = 0;
    while (acc >= step && n < 3) {
      acc -= step; n++;
      const t = t0 + U(0, 0.012);
      if (!this.allowGrain(t, 3)) continue;
      const f = (1350 - 500 * clamp01(depth / 0.2)) * pitch(m) * U(0.85, 1.15);
      this.play(this.pick("fric"), t, db(U(-47, -39)), f / 1000, LO);
    }
    this.squeezeAcc.set(key, Math.min(acc, step * 2));
  }

  /** Forget a ball's texture accumulators (on release / respawn). */
  resetTexture(key: string) { this.runAcc.delete(key); this.squeezeAcc.delete(key); }

  /** A ball left the table: drop everything kept for it. */
  forget(key: string) { this.resetTexture(key); this.runTotal.delete(key); }

  // ---------- the plate damage model ----------

  /**
   * `n` plates cracked at once under the finger (`intensity` 0..1 = how hard / how far the crack opened).
   * 1 plate lightly → a single "콱"; several hard → "콰자자작" with the heavy body. Bigger cracks are lower.
   */
  crack(n: number, intensity: number, m: number, sub = false): number[] {
    if (!this.running || !(n > 0)) return [];
    m = pitch(m);
    const k = clamp01(intensity), ln = Math.log2(Math.max(1, n));
    const hits = Math.max(1, Math.min(4, Math.round(1 + 0.9 * ln + 1.2 * k + U(-0.45, 0.45))));
    const levelDb = -14 + 11 * k + Math.min(2, 0.7 * ln);
    const on = this.crunch(hits, levelDb, m * (1.1 - 0.2 * k) * (n > 6 ? 0.95 : 1), { sub, heavy: k > 0.8 && n >= 3 });
    if (on.length) this.settle(this.now() + LEAD + on[on.length - 1] / 1000, m, k > 0.4 ? 2 : 1);
    return on;
  }

  /** Hairlines appearing around the press: a tiny high tick (sometimes two, "틱틱"), at most one every 45 ms. */
  hairline(m: number) {
    if (!this.running) return;
    const t = this.now() + LEAD;
    if (t - this.lastHair < 0.045) return;
    this.lastHair = t; m = pitch(m);
    // kept well under the crunches: a faint tick, never a sizzle
    this.grain(t, m * 1.1, db(U(-39, -33)));
    if (Math.random() < 0.35) this.grain(t + U(0.006, 0.016), m * 1.2, db(U(-44, -39)));
  }

  /**
   * A plate breaks free ("트득-똑"): edge creak, then the snap. `size` 0..1 (bigger = lower, louder; ≥ 0.55 adds a
   * crunch hit for weight). Several in one frame are spread 25–45 ms apart instead of stacking. Returns hit onsets (ms);
   * the first may be > 0 when spread.
   */
  flakeOff(size: number, m: number, sub = false): number[] {
    if (!this.running) return [];
    const s = clamp01(size), now = this.now() + LEAD;
    m = pitch(m);
    const t = Math.max(now, this.flakeNext);
    if (t - now > 0.14) return []; // a pile-up: the ones already scheduled carry it
    this.flakeNext = t + U(0.025, 0.045);
    const r = m * (1.25 - 0.4 * s) * U(0.93, 1.07), v = this.pick("peel"), lvl = db(-18 + 11 * s);
    if (!this.play(v, t, lvl, r, MAIN)) return [];
    const off = (t - now) * 1000, snap = v.marks[1] / r, ts = t + snap / 1000;
    if (s >= 0.55) this.play(this.pick("ja"), ts + 0.001, lvl * 0.4, m * 0.9 * U(0.95, 1.05), MAIN);
    if (sub) this.play(this.bank!.sub[0], ts + 0.002, lvl * (0.25 + 0.3 * s), U(0.97, 1.03), MAIN);
    return [off, off + snap];
  }

  /**
   * A falling piece lands / hits something (`size`, `speed` 0..1). Calls made in the same frame are gathered, the loudest
   * plays first and the rest follow 8–15 ms apart ("타다닥"): at most 5 per frame and 6 per 100 ms, quieter when many
   * land together.
   */
  shardLand(size: number, speed: number, m = 1) {
    if (!this.running) return;
    const sp = clamp01(speed), sz = clamp01(size);
    if (sp < 0.04) return;
    this.landQ.push({ size: sz, sp, m: pitch(m), e: sp * sp * (0.5 + sz) });
    if (!this.landQueued) { this.landQueued = true; queueMicrotask(this.flushLands); }
  }

  private flushLands = () => {
    this.landQueued = false;
    const q = this.landQ;
    try {
      if (!this.running || !q.length) return;
      q.sort((a, b) => b.e - a.e);
      const now = this.now() + LEAD, lt = this.landTimes;
      while (lt.length && lt[0] < now - 0.1) lt.shift();
      let at = Math.max(now, this.landNext);
      // at most 5 per batch and 6 per 100 ms: a settling pile clatters, it never turns into a rain of ticks
      for (let i = 0; i < Math.min(5, q.length) && at - now <= 0.06 && lt.length < 6; i++) {
        const l = q[i], big = l.size > 0.5;
        if (lt.length >= 4 && l.sp < 0.35) continue; // busy: small bounces are masked anyway
        const duck = 1 / (1 + 0.3 * lt.length);
        const lvl = db(-34 + 22 * l.sp + 4 * l.size) * duck;
        const r = l.m * (big ? 1.3 - 0.5 * l.size : 1.45 - 0.8 * l.size) * U(0.92, 1.08);
        if (this.play(this.pick(big ? "landL" : "landS"), at, lvl, r, LO)) { lt.push(at); at += U(0.008, 0.015); }
      }
      this.landNext = at;
    } finally {
      q.length = 0; // even if something threw: a bad entry must never stay queued
    }
  };

  /**
   * The finger pressing bare clay: a soft wet "뭉/쭈욱" (no highs, so never a hiss). `amount` 0..1 is how much the clay
   * gave; small per-frame amounts add up. At most one squish per 220 ms.
   */
  squish(amount: number, m: number) {
    if (!this.running || !(amount > 0)) return; // a NaN would stick in the accumulator for good
    this.squishAcc = Math.min(1.5, this.squishAcc + amount);
    const t = this.now() + LEAD;
    if (t - this.lastSquish < 0.22 || this.squishAcc < 0.08) return;
    const a = Math.min(1, this.squishAcc);
    this.squishAcc = 0; this.lastSquish = t;
    this.play(this.pick("squish"), t, db(-25 + 13 * a), pitch(m) * (1.1 - 0.25 * a) * U(0.9, 1.08), LO);
  }

  // ---------- events used by the ball ----------

  /** First crack under the finger: "콰작!" */
  firstSplit(m: number, sub = false) { return this.crunch(2, -5, m, { sub }); }

  /** The big 뽀각: "콰자작!" or "콰자자작!" with the heavy body, loudest event of the toy. */
  pop(m: number, sub = false) { return this.crunch(Math.random() < 0.5 ? 3 : 4, -1.5, m, { heavy: true, sub }); }

  /** Later cracks while the finger keeps pressing: a varied "콰작 / 콰자작 / 콰자자작". Deeper as the shell runs out. */
  burst(stage: 1 | 2 | 3, m: number, sub = false) {
    const r = Math.random();
    const hits = r < 0.34 ? 2 : r < 0.74 ? 3 : 4;
    return this.crunch(hits, [-4, -6, -8][stage - 1], m * [1, 0.92, 0.84][stage - 1], { sub });
  }

  /** A big slab finally giving way. */
  secondaryPop(m: number, sub = false) { return this.crunch(3, -3, m * 0.9, { heavy: true, sub }); }

  /** The crush: the whole shell gives way at once — the deepest "콰자자작!" of the toy. */
  crushed(m: number, sub = false) {
    const on = this.crunch(4, -0.5, m * 0.86, { heavy: true, sub });
    // 뭉개짐: the clay core giving way under the finger right after the shell — a soft, wet, low squish
    if (on.length) this.play(this.pick("squish"), this.now() + 0.16, db(-10), pitch(m) * 0.75, MAIN);
    return on;
  }

  /** The crushed lump falling apart: a light, quick "사가작". */
  crumble(m: number) { return this.crunch(2, -17, m * 1.2); }

  /** Crunching a loose flake into the clay: a small "오독". */
  flake(m: number, sub = false) { return this.crunch(Math.random() < 0.5 ? 1 : 2, -14, m * 1.15, { sub }); }

  /** How long until a crunch scheduled now is actually heard (scheduling lead + output latency), so haptics can wait for it. */
  hapticDelayMs() {
    const ctx = this.ctx as (AudioContext & { outputLatency?: number }) | null;
    if (!ctx || !this.running) return 0;
    return Math.round((LEAD + (ctx.outputLatency || ctx.baseLatency || 0)) * 1000);
  }

  /** A hairline crack appearing before the shell opens: a tiny, quiet tick. */
  craze(m: number) {
    if (!this.running) return;
    this.grain(this.now() + LEAD, pitch(m), db(-22));
  }

  /** Short filtered-noise grain (neck snap 툭, tap 톡, release). */
  tick(f: number, durMs: number, levelDb: number, q = 2) {
    if (!this.running || !(f > 0 && durMs > 0 && q > 0) || !Number.isFinite(f + durMs + levelDb + q)) return;
    this.noiseHit(this.now(), "bandpass", f * U(0.9, 1.1), q, db(levelDb), durMs / 3, durMs);
  }

  /** Wet 쭙 while kneading clay: noise → lowpass → bandpass, plus a low sine. Quiet; only while the finger moves. */
  wetStart(key: string) {
    if (!this.running || this.layers.has(key)) return;
    const ctx = this.ctx!, t = this.now();
    const src = ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 700;
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 380; bp.Q.value = 3.5;
    const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = 100;
    const og = ctx.createGain(); og.gain.value = 0.5;
    const g = ctx.createGain(); g.gain.setValueAtTime(0, t);
    src.connect(lp); lp.connect(bp); bp.connect(g); o.connect(og); og.connect(g); g.connect(this.master!);
    src.start(t, Math.random()); o.start(t);
    const a = new Float32Array([700, 380, 100, 0]);
    this.layers.set(key, { nodes: [src, o], p: [lp.frequency, bp.frequency, o.frequency, g.gain], a, b: a.slice(), t0: t, t1: t });
  }

  /** Where a layer's param `i` is at `t` on the ramp we scheduled (never its lagging `.value`). */
  private layerAt(l: Layer, i: number, t: number) {
    return t >= l.t1 ? l.b[i] : l.a[i] + ((l.b[i] - l.a[i]) * (t - l.t0)) / (l.t1 - l.t0);
  }

  wetDrive(key: string, speed: number, strokeT: number) {
    const l = this.layers.get(key); if (!l || !this.ctx) return;
    const t = this.now(), u = clamp01(strokeT), to = this.tgt;
    to[0] = 700 - 540 * u; to[1] = 380 - 200 * u; to[2] = 100 - 52 * u; to[3] = speed > 20 ? db(-20) : 0;
    for (let i = 0; i < 4; i++) {
      const p = l.p[i], v = this.layerAt(l, i, t);
      p.cancelScheduledValues(t); p.setValueAtTime(v, t); p.linearRampToValueAtTime(to[i], t + 0.06);
      l.a[i] = v; l.b[i] = to[i];
    }
    l.t0 = t; l.t1 = t + 0.06;
  }

  stopLayer(key: string, fadeMs = 60) {
    const l = this.layers.get(key); if (!l || !this.ctx) return;
    const t = this.now(), g = l.p[3], end = t + (fadeMs > 0 && fadeMs < 1e4 ? fadeMs : 0) / 1000;
    g.cancelScheduledValues(t);
    g.setValueAtTime(this.layerAt(l, 3, t), t);
    g.linearRampToValueAtTime(0, end);
    for (const n of l.nodes) { try { (n as AudioScheduledSourceNode).stop(end + 0.02); } catch { /* */ } }
    this.layers.delete(key);
  }

  /** ≤ 80 ms total: one sticky release pop and the kneading layer fades out. */
  release(ballKey: string) {
    if (!this.ctx) return;
    if (this.running) this.tick(300, 40, -18);
    this.stopLayer(ballKey + ":wet");
    this.resetTexture(ballKey);
  }
}
